import { useCallback, useMemo } from 'react';

import SimpleTableCell from '@/components/SimpleTable/SimpleTableCell';
import SimpleTableRow from '@/components/SimpleTable/SimpleTableRow';
import { SortDirectionEnum } from '@/models/gen/graphql';
import { useAccidentHistory } from '@/features/AccidentHistory';

const AccidentHistoryTableColumns = (): JSX.Element => (
  <SimpleTableRow header>
    <SelectAllCell />
    <SortableCell name="STATION_TEXT">Airport</SortableCell>
    <SortableCell name="ACCIDENT_DATETIME">Date / Time</SortableCell>
    <SortableCell name="CREATED_AT">Created</SortableCell>
    <SortableCell name="TRIP_SERVICER_IATA_AIRLINE_CODE">AL</SortableCell>
    <SortableCell name="TRIP_FLIGHT_NAME">FLT #</SortableCell>
    <SortableCell name="TRIP_SCHEDULED">Sch Time</SortableCell>
    <SortableCell name="VEHICLE_TRACKING_ID">Van</SortableCell>
    <SortableCell name="DRIVER_NAME">Employee</SortableCell>
    <SortableCell name="DRIVER_EMPLOYEE_ID">ID #</SortableCell>
    <SortableCell name="SAFETY_NAME">Manager</SortableCell>
    <SortableCell name="ANYONE_INJURED">Injury?</SortableCell>
    <SortableCell name="WAS_CREW_INVOLVED">Crew Onboard?</SortableCell>
  </SimpleTableRow>
);

const SelectAllCell = (): JSX.Element => {
  const [{ accidents, selected }, setState] = useAccidentHistory(({ accidents, selected }) => ({ accidents, selected }));

  const isSelectAll = useMemo((): boolean => {
    if (selected.size !== accidents.size) return false;
    for (const id of selected.keys()) {
      if (!accidents.has(id)) return false;
    }
    return true;
  }, [accidents, selected]);

  const onClick = useCallback(
    (): void => setState((current) => ({ ...current, selected: isSelectAll ? new Map() : accidents })),
    [accidents, isSelectAll, setState]
  );

  return <SimpleTableCell.Select checked={isSelectAll} onChange={onClick} />;
};

const SortableCell = ({ name, children }: { name: string; children: React.ReactNode }): JSX.Element => {
  const [{ sortBy, sortDirection }, setState] = useAccidentHistory(({ sortBy, sortDirection }) => ({ sortBy, sortDirection }));
  const onSort = (name: string, direction: SortDirectionEnum): void =>
    setState((current) => ({ ...current, sortBy: name, sortDirection: direction }));
  return (
    <SimpleTableCell.Sortable name={name} sortBy={sortBy} sortDirection={sortDirection} onSort={onSort}>
      {children}
    </SimpleTableCell.Sortable>
  );
};

export default AccidentHistoryTableColumns;
